import { derived, get, writable } from "svelte/store"
import {
    doc,
    collection,
    query,
    onSnapshot,
    setDoc,
    deleteField,
    deleteDoc,
    writeBatch,
} from "firebase/firestore"
import type { QuerySnapshot, QueryDocumentSnapshot } from "firebase/firestore"
import { user, db, userStatus, getUserProfileDocPath } from "@utils/underhood/firebase"
import { LCH } from "@utils/highlights/color"
import { createNotice } from "@components/bottomPopup/Notice.module"
import { ObjectEntries } from "@utils/helpers/object-entries"
import { urlFromPath } from "./pageTracker"
import { WordReference, type ReferenceWeight, type WordPath } from "@circleofa/acim-engine"
import { randomString } from "@utils/helpers/random-string"
import { registerUserStatusCallback } from "@utils/helpers/unload-promise"
import { logWrite } from "@utils/log"
import { debounceCancel, debounceFn } from "@utils/helpers/debounce"
import { acim } from "@utils/acim"

/**
 *
 * Highlights stored in /users/[userid]/highlighters/[highlighterid]/highlights/[highlights]
 *
 * /users/[userid]/highlighters/[highlighterid]
 *  label       // some string
 *  chroma      // 0-100
 *  hue         // 0-100
 *  lightness   // 0-100 luminosity for light highlgihter
 *  darkness
 *
 * /users/[userid]/highlighters/[highlighterid]/highlights/all
 *  [id]:
 *      s //start
 *      e //end
 *      n //note?
 *      t //time
 *
 */
//

export const highlightsReady = writable(false)

////////////
//
//  HIGHLIGHTERS
//

export type HighlighterID = string

export type FSHighlighter = {
    id: HighlighterID
    label: string
    chroma: number
    darkness: number
    lightness: number
    hue: number
}

const highlightCollection = "highlighters"
function getHighlightsCollection() {
    if (!get(user)) throw new Error("user not logged in")
    return collection(db, getUserProfileDocPath(), highlightCollection)
}

function getHighlighterDocument(id: string) {
    if (!get(user)) throw new Error("user not logged in")
    return doc(db, getHighlightsCollection().path, id)
}

/**
 * @returns Returns the destination of where the highlights should be saved/retrieved
 */
function saveLocation() {
    return get(userStatus) == "verified" ? "server" : "local"
}

export function getAllNotes() {
    return getAllHighlights()
        .filter((n) => n.hasNote)
        .sort((a, b) => b.time - a.time)
}

export function getAllHighlights() {
    return [...get(highlighters).values()].flatMap((highlighter) => [
        ...highlighter.highlights.values(),
    ])
}

export const highlighters = writable<Map<HighlighterID, Highlighter>>(new Map())
export const highlightersSorted = derived(
    highlighters,
    (highlighters) => {
        return new Map([...highlighters.entries()].sort(([a], [b]) => a.localeCompare(b)))
    },
    new Map<HighlighterID, Highlighter>(),
)
export const refreshHighlighterStore = () => highlighters.update((h) => h)

export class Highlighter {
    id: HighlighterID
    #label: string
    #chroma: number = 0
    #hue: number = 0
    #light: LCH | undefined
    #dark: LCH | undefined
    #lightness = 0
    #darkness = 0
    highlights = new Map<HighlightID, Highlight>()

    constructor(highlighter: FSHighlighter) {
        this.id = highlighter.id
        this.#label = highlighter.label
        this.#darkness = highlighter.darkness
        this.#chroma = highlighter.chroma
        this.#lightness = highlighter.lightness
        this.#hue = highlighter.hue
    }

    static fromFullhighlighter(highlighterData: FSFullHighlighter) {
        const highlighter = new Highlighter(highlighterData.meta)
        const { meta, ...fs } = highlighterData
        highlighter.updateHighlightsFromFirestore(fs)
        return highlighter
    }

    static labelSaveTimeout = -1

    get label() {
        return this.#label
    }
    set label(label: string) {
        this.#label = label
        this._render()
    }

    get hue() {
        return this.#hue
    }
    set hue(hue: number) {
        this.#hue = hue
        this._render()
    }

    get chroma() {
        return this.#chroma
    }
    set chroma(chroma: number) {
        this.#chroma = chroma
        this._render()
    }

    get lightness() {
        return this.#lightness
    }
    set lightness(lightness: number) {
        this.#lightness = lightness
        this._render()
    }

    get darkness() {
        return this.#darkness
    }
    set darkness(darkness: number) {
        this.#darkness = darkness
        this._render()
    }

    getHighlightList(): Record<HighlightID, FSHighlight> {
        const fsList: Record<HighlightID, FSHighlight> = {}
        for (const [id, highlight] of this.highlights) {
            fsList[id] = highlight.toFirestore()
        }
        return fsList
    }

    setHighlightsFromList(list: Record<HighlightID, FSHighlight>) {
        const highlights = new Map<HighlightID, Highlight>()
        for (const [highlightID, fshighlight] of ObjectEntries(list)) {
            // Catching in case of rogue highlight
            try {
                const highlight = Highlight.fromFirestore(highlightID, this.id, fshighlight)
                // This adds a bit of logic to throw in case the highlights are out of order
                highlight.start.referenceWeight
                highlight.end.referenceWeight

                highlights.set(highlightID, highlight)
            } catch (e) {
                console.warn(e)
                console.warn("Rogue highlight detected and not included")
            }
        }
        this.highlights = highlights
    }

    /**
     * Includes highlight list
     */
    toFullFirestore(): LSHighlights[keyof LSHighlights] {
        return {
            meta: this.toFirestore(),
            ...this.getHighlightList(),
        }
    }

    /**
     * Meta only
     * USE MERGE!!!!
     */
    mergeFirestore() {
        return {
            meta: this.toFirestore(),
        }
    }

    _processCSS() {
        this.#light = new LCH([this.#lightness, this.#chroma, this.#hue])
        this.#dark = new LCH([this.#darkness, this.#chroma, this.#hue])
    }

    /**
     * Renders CSS and saves to DB via debounce
     */
    _render() {
        this._processCSS()
        refreshHighlighterStore()
        const id = this.id + "-meta"
        debounceFn(id, () => saveHighlighter(this), 2000)

        // Important for cancelling if user logs out or in
        debounceCancelList.add(id)
    }

    /**
     * Updates the full highlighter preserving already-existing objects
     * @param highlighterData full highlighter data including highlighter meta & highlights
     * @returns true if there was a change, false if not
     */
    updateFromFirestore(highlighterData: FSFullHighlighter): boolean {
        const { meta, ...fs } = highlighterData
        let changed = this.updateMetaFromFirestore(meta)
        changed = this.updateHighlightsFromFirestore(fs) || changed
        return changed
    }

    /**
     * updates without saving to firestore
     * @returns true if there was a change false if not
     */
    updateMetaFromFirestore(fs: FSHighlighter) {
        let changed = false
        if (
            this.#label != fs.label ||
            this.#darkness != fs.darkness ||
            this.#chroma != fs.chroma ||
            this.#lightness != fs.lightness ||
            this.#hue != fs.hue
        )
            changed = true
        this.#label = fs.label
        this.#darkness = fs.darkness
        this.#chroma = fs.chroma
        this.#lightness = fs.lightness
        this.#hue = fs.hue
        if (changed) this._processCSS()
        return changed
    }

    /**
     * Updates highlights in highlighter, returns true if any changes, false if not
     */
    updateHighlightsFromFirestore(newHighlights: Record<HighlightID, FSHighlight>): boolean {
        // Checking for new ones, changes, or deletes
        let changed = false
        for (const [id, fsHighlight] of ObjectEntries(newHighlights)) {
            const already = this.highlights.get(id)
            if (already) {
                // There is one already - check if changed
                changed = already.updateFromFirestore(fsHighlight) || changed
            } else {
                changed = true
                // Instantiate new highlight, but also make sure
                try {
                    this.highlights.set(id, Highlight.fromFirestore(id, this.id, fsHighlight))
                } catch (e) {
                    // Rogue highlight detected
                    console.warn(e)
                    console.warn(
                        "Rogue highlight detected while updating highlights from firestore",
                    )
                }
            }
        }

        // this.highlights now contains updated highlights, additional new highlights, AND also highlights that should be deleted

        const newKeys = Object.keys(newHighlights) // # of new highlights
        if (newKeys.length < this.highlights.size) {
            // # of new highlights < # of old highlights + new highlights = some were deleted
            changed = true
            const deleteKeys: HighlightID[] = []
            for (const [id, highlight] of this.highlights) {
                if (!newKeys.includes(id)) deleteKeys.push(id)
            }
            deleteKeys.forEach((id) => this.highlights.delete(id))
        }
        return changed
    }

    get light() {
        if (!this.#light) this._processCSS()
        return this.#light!.css
    }

    get dark() {
        if (!this.#dark) this._processCSS()
        return this.#dark!.css
    }

    toFirestore(): FSHighlighter {
        return {
            id: this.id,
            label: this.label,
            hue: this.#hue,
            chroma: this.chroma,
            darkness: this.#darkness,
            lightness: this.#lightness,
        }
    }
}

// These actaully aren't unique at this point, but I don't think anything majorly
// bad will happen in the rare case that two have the same ID. I think they'll
// just overwrite each other, and the person just won't have a color unless
// they want to make a new one
const yellow = {
    id: newUniqueHighlighterID(),
    label: "Yellow",
    chroma: 49,
    hue: 86.5,
    lightness: 100,
    darkness: 26,
}
const red = {
    id: newUniqueHighlighterID(),
    label: "Red",
    chroma: 33,
    hue: 44,
    lightness: 78,
    darkness: 23,
}
const pink = {
    id: newUniqueHighlighterID(),
    label: "Pink",
    chroma: 36,
    hue: 357,
    lightness: 75,
    darkness: 25,
}
const purple = {
    id: newUniqueHighlighterID(),
    label: "Purple",
    chroma: 23,
    hue: 308,
    lightness: 70,
    darkness: 20,
}
const blue = {
    id: newUniqueHighlighterID(),
    label: "Blue",
    chroma: 26,
    hue: 232,
    lightness: 82,
    darkness: 18,
}

export function newHighlighter(): HighlighterID {
    const id: HighlighterID = newUniqueHighlightID()
    const highlighter = new Highlighter({
        id,
        label: "Untitled",
        chroma: 48,
        hue: 104,
        lightness: 95,
        darkness: 20,
    })
    highlighters.update((h) => {
        h.set(id, highlighter)
        return h
    })
    saveHighlighter(highlighter)
    return id
} // ✔

/**
 * Also deletes all highlights
 */
export async function deleteHighlighter(highlighterID: string) {
    if (get(highlighters).size <= 1) {
        createNotice({
            headline: "Oops!",
            detail: "You have to have at least one highlighter!",
        })
        return
    }

    if (saveLocation() == "server") {
        const del = await deleteDoc(getHighlighterDocument(highlighterID))
    } else {
        // not verified, deleting on LS
        const originalHighlights = getHighlightsLocally()
        delete originalHighlights[highlighterID]
        setHighlightsLocally(originalHighlights)
    }

    highlighters.update((h) => {
        h.delete(highlighterID)
        return h
    })
    return
} // ✔

/**
 * Saves a highlighter metadata
 * @param highlighter
 */
async function saveHighlighter(highlighter: Highlighter) {
    const meta = highlighter.toFirestore()
    if (saveLocation() == "server") {
        const doc = getHighlighterDocument(highlighter.id)
        const data = { meta }
        logWrite(doc.path, data)
        await setDoc(doc, data, { merge: true })
    } else {
        const highlightersLocal = getHighlightsLocally()
        if (!(highlighter.id in highlightersLocal)) {
            highlightersLocal[highlighter.id] = { meta }
        } else {
            highlightersLocal[highlighter.id].meta = meta
        }
        setHighlightsLocally(highlightersLocal)
    }
} // ✔

//
//
// Stylesheet
//
//
const highlightStyles = document.head.appendChild(document.createElement("style"))
export const getClassForHighlighter = (id: string) => `__h-${id}`
let lastCSS = ""
function updateStylesheet() {
    const highlighterClasses = [...get(highlighters).values()]
    const currentCSS = highlighterClasses
        .map((highlighter) => highlighter.id + highlighter.light + highlighter.dark)
        .join("")
    if (currentCSS == lastCSS) return
    lastCSS = currentCSS
    highlightStyles.innerHTML = highlighterClasses
        .map((highlighter) => writeCSS(highlighter))
        .join("")
}
highlighters.subscribe(updateStylesheet)

function writeCSS(highlighter: Highlighter) {
    const highlighterID = highlighter.id
    const className = "." + getClassForHighlighter(highlighterID)
    return `
        ${className} {
                background-color: ${highlighter.light};
                fill: ${highlighter.light}
            }
        .darkmode ${className} {
                background-color: ${highlighter.dark};
                fill: ${highlighter.dark}
            }
    `
}

/////////
//
//    SELECTED HIGHLIGHTER
//

export const selectedHighlighter = writable([...get(highlighters).keys()][0])
highlighters.subscribe((highlighters) => {
    const selected = get(selectedHighlighter)
    if (!highlighters.get(selected)) {
        selectedHighlighter.set([...highlighters.keys()][0])
    }
})
export function changeSelected(id: HighlighterID) {
    selectedHighlighter.set(id)
}

////////////
//
//  HIGHLIGHTS
//

export type HighlightID = `_${string}`
type FSHighlight = {
    s: WordPath // start
    e: WordPath // end
    n?: string // note
    t: number // timestamp
}

export class Highlight {
    // id: HighlightID
    // start: WordReference
    // end: WordReference
    // highlighter: HighlighterID
    // time: number
    // note: string

    constructor(
        public readonly id: HighlightID,
        public start: WordReference,
        public end: WordReference,
        public highlighter: HighlighterID,
        public time: number,
        public note = "",
    ) {
        // this.id = id
        // this.start = start
        // this.end = end
        // this.note = note
        // this.time = time
        // this.highlighter = highlighter
    }

    get hasNote() {
        return this.note?.length > 0
    }

    isEqual(other: Highlight) {
        if (this.id !== other.id) return false
        if (!this.start.isEqual(other.start)) return false
        if (!this.end.isEqual(other.end)) return false
        if (this.highlighter !== other.highlighter) return false
        return true
    }

    getHighlighter() {
        const highlighter = get(highlighters).get(this.highlighter)
        if (highlighter === undefined)
            throw new Error(
                `Highlight ${this.id} is orphaned. Cannot find highlighter with ID ${this.highlighter}`,
            )
        return highlighter
    }

    toFirestore(): FSHighlight {
        const rtn: FSHighlight = {
            s: this.start.path,
            e: this.end.path,
            t: this.time,
        }
        if (this.note.length) {
            rtn.n = this.note
        }
        return rtn
    }

    /**
     * Updates from firestore, returns true if there was a change
     */
    updateFromFirestore({ s: start, e: end, t: time, n: note }: FSHighlight) {
        let changed = false
        if (note === undefined) note = ""
        if (start === undefined || end === undefined)
            throw new Error(`Time undefined for for highlight ${this.id}`)
        if (time === undefined) throw new Error(`Time undefined for for highlight ${this.id}`)

        // If there are changes to the book, sometimes the sentence structure changes
        //
        try {
            if (start != this.start.path) {
                this.start = new WordReference({ path: start, acim })
                changed = true
            }
            if (end != this.end.path) {
                this.end = new WordReference({ path: end, acim })
                changed = true
            }
            if (time != this.time) {
                this.time = time
                changed = true
            }
            if (note != this.note) {
                this.note = note
                changed = true
            }
        } catch (e) {
            console.warn(e)
            console.log("Deleting rogue highlight...")

            // Since we're updating, we can let the async deletion take place and just hold the original value
            deleteHighlight(this.id)
        }
        return changed
    }

    /**
     * IMPORTANT: This assumes that you have already checked it's not a rogue highlight. Please catch it!
     * @param id
     * @param highlighterID
     * @param highlight
     * @returns
     */
    static fromFirestore(id: HighlightID, highlighterID: HighlighterID, highlight: FSHighlight) {
        return new Highlight(
            id,
            new WordReference({ path: highlight.s, acim }),
            new WordReference({ path: highlight.e, acim }),
            highlighterID,
            highlight.t,
            highlight.n,
        )
    }

    get commonAncestor() {
        let common = ""
        for (let i = 0; i < this.start.path.length; i++) {
            if (this.start.path[i] != this.end.path[i]) break
            common += this.start.path[i]
        }
        // remove extra stuff
        common = common.replace(/[^a-zA-Z0-9]*$/g, "")
        return common
    }

    /**
     * TODO make sure there are no looses here
     * @throws
     */
    get url() {
        return urlFromPath(this.start.path.split("~")[0])
    }

    async changeHighlighter(newHighlighter: Highlighter | null = null) {
        if (newHighlighter === null) {
            const newHighlighterID = get(selectedHighlighter)
            newHighlighter = get(highlighters).get(newHighlighterID) ?? null
            if (newHighlighter === null)
                throw new Error(
                    `Changing highlight ${this.id} to highlighter - highlight not supplied and could not locate current highlighter ${newHighlighterID}`,
                )
        }
        console.log("cahgning highlighter....")
        return await switchHighlighter(this, newHighlighter)
    }
}

type FSFullHighlighter = {
    meta: FSHighlighter
    [highlightID: HighlightID]: FSHighlight
}

const LSKey = "highlightsv3" as const
type LSHighlights = {
    [highlighterID: HighlighterID]: FSFullHighlighter
}

function localStorageDefaults() {
    const storageObject: LSHighlights = {}
    ;[yellow, red, pink, purple, blue].forEach((highlighter) => {
        storageObject[highlighter.id] = {
            meta: highlighter,
        }
    })
    setHighlightsLocally(storageObject)
} // ✔

/**
 * Make sure it's
 * @returns
 */
const getHighlightsLocally = () => {
    let json = localStorage.getItem(LSKey)
    if (json === null) {
        // Create if none
        localStorageDefaults()
        json = localStorage.getItem(LSKey)
        if (json === null) throw new Error("Highlights in localStorage null after setting defaults")
    }
    return JSON.parse(json) as unknown as LSHighlights
}
const setHighlightsLocally = (obj: LSHighlights) => localStorage.setItem(LSKey, JSON.stringify(obj))

// Reacts to highlighters but doesn't go through the work of creating a new map, just use the function
// like $highlights(something)
export const highlights = derived(highlighters, () => findHighlight, findHighlight)

function setHighlighterStoreFromLS() {
    const localHighlights = getHighlightsLocally()
    const newHighlighters = new Map<string, Highlighter>()
    Object.entries(localHighlights).forEach(([highlighterID, highlighterInfo]) => {
        const { meta, ...fs } = highlighterInfo
        const highlighter = new Highlighter(meta)
        newHighlighters.set(highlighterID, highlighter)
        highlighter.setHighlightsFromList(fs)
    })
    highlighters.set(newHighlighters)
} // ✔

async function transferHighlights() {
    // Transfers from local to online. Deletes local if success. Pls auth prior
    const currentHighlights = getHighlightsLocally()
    const batch = writeBatch(db)
    for (const [highlighterID, highlighterInfo] of Object.entries(currentHighlights)) {
        const doc = getHighlighterDocument(highlighterID)
        logWrite(doc.path, highlighterInfo, "transfer highlights")
        batch.set(doc, highlighterInfo)
    }
    setHighlighterStoreFromLS() // this takes the highlighters and sets from LS
    localStorageDefaults() // this resets LS now that we're working with FS
    const commit = await batch.commit()
    return commit
} // ✔

// /**
//  * @param highlighterID The ID of the highlighter not highlight
//  */
// function getHighlightDocument(highlighterID : string) {
//     return doc(db, getHighlighterDocument(highlighterID).path, "highlights/all")
// } // ✔

export function findHighlight(id: HighlightID): Highlight | null {
    for (const [_, highlighter] of get(highlighters)) {
        const rtn = highlighter.highlights.get(id)
        if (rtn) return rtn
    }
    return null
} // ✔

// Called when saving to either firestore or local database (this function decides based on auth status)
/**
 * This should be called AFTER the change has been made to the highlight class.
 * This does not refresh the highligher store
 */
export async function updateHighlight(highlight: Highlight) {
    // This is called when we want to update the database
    const fsHighlight = highlight.toFirestore()

    if (saveLocation() == "server") {
        const doc = getHighlighterDocument(highlight.highlighter)
        const data = { [highlight.id]: fsHighlight }
        logWrite(doc.path, data)
        await setDoc(doc, data, { merge: true })
    } else {
        // Working with localstorage
        const storage = getHighlightsLocally()
        storage[highlight.highlighter][highlight.id] = fsHighlight
        setHighlightsLocally(storage)
    }
} // ✔

async function switchHighlighter(highlight: Highlight, to: Highlighter) {
    const from = highlight.highlighter

    // Update highlighters store
    highlighters.update((highlighters) => {
        // Delete from old highlighter
        highlighters.get(highlight.highlighter)?.highlights.delete(highlight.id)

        // Switch highlight in class
        highlight.highlighter = to.id

        // Append to new highlighte
        to.highlights.set(highlight.id, highlight)

        return highlighters
    })

    // Prep for firestore
    const fsHighlight = highlight.toFirestore() // highlighter id should already be changed

    // Update storage
    if (saveLocation() == "server") {
        const oldDoc = getHighlighterDocument(from)
        const newDoc = getHighlighterDocument(to.id)

        const batch = writeBatch(db)
        const old = { [highlight.id]: deleteField() }
        logWrite(oldDoc.path, old)
        const a = batch.set(oldDoc, old, { merge: true })

        const newd = { [highlight.id]: fsHighlight }
        logWrite(oldDoc.path, newd)
        const b = batch.set(newDoc, newd, { merge: true })

        batch.commit()
    } else {
        // Working with localstorage
        const storage = getHighlightsLocally()

        delete storage[from][highlight.id]
        storage[to.id][highlight.id] = fsHighlight

        setHighlightsLocally(storage)
    }
} // ✔

export function newUniqueHighlightID() {
    let newID: HighlightID | null = null
    while (!newID || get(highlights)(newID)) newID = ("_" + randomString()) as HighlightID
    return newID
}
export function newUniqueHighlighterID() {
    let newID: HighlighterID | null = null
    while (!newID || get(highlighters)?.get(newID)) newID = randomString()
    return newID
}

/**
 * creates new highlight, start and end must be in path format (eg T-xx.xx:x~x)
 */
export function newHighlight(start: WordPath, end: WordPath): HighlightID {
    const id = newUniqueHighlightID()

    // This doesn't need to get checked for rogue highlights because
    // it theoretically should never fail. And if it does, I want to
    // know about it
    const highlight = Highlight.fromFirestore(id, get(selectedHighlighter), {
        s: start,
        e: end,
        t: Date.now(),
    })

    highlighters.update((highlighters) => {
        const highlighter = highlighters.get(get(selectedHighlighter))
        if (highlighter === undefined)
            throw new Error(`Selected highlighter can't be found: ${get(selectedHighlighter)}`)
        highlighter.highlights.set(id, highlight)
        return highlighters
    })

    updateHighlight(highlight)
    return id
} // ✔

export async function deleteHighlight(highlightID: HighlightID) {
    const highlighter = get(highlights)(highlightID)?.getHighlighter()
    if (highlighter === undefined)
        throw new Error(
            `Highlight (${highlightID}) highlighter could not be found: ${get(highlights)(
                highlightID,
            )}`,
        )

    highlighters.update((highlighters) => {
        highlighter.highlights.delete(highlightID)
        return highlighters
    })

    if (saveLocation() == "server") {
        const doc = getHighlighterDocument(highlighter.id)
        const data = { [highlightID]: deleteField() }
        logWrite(doc.path, data)
        await setDoc(doc, data, { merge: true })
    } else {
        const storage = getHighlightsLocally()
        delete storage[highlighter.id][highlightID]
        setHighlightsLocally(storage)
    }
} // ✔

/**
 *   INIITIAL LOAD & LISTENERS
 *
 */
//

/**
 * This is important as it helps to remove any pending
 * writes between log in/out to prevent overwriting different
 * users or transferring data between guests/users.
 */
const debounceCancelList: Set<Parameters<typeof debounceFn>[0]> = new Set()

/**
 * Merge from database should be called to handle firestore updates, expect them to come at any time
 * because firestore uses
 * @param querySnapshot
 * @returns
 */
async function mergeFromDatabase(querySnapshot: QuerySnapshot<FSFullHighlighter>) {
    // First check if empty
    if (querySnapshot.empty) {
        // Empty means we'll upload what we have already in store
        await transferHighlights()
        return
    }

    // Grab current highlighers and create new map for comparison
    const currentHighlighters = get(highlighters)
    const newHighlighters = new Map<HighlighterID, Highlighter>()

    let highlightersChanged = false // To track if there are any changes

    querySnapshot.forEach((doc) => {
        const highlighterID = doc.id
        const highlighterData = doc.data()

        let thisHighlighterChanged = false // Tracking current highlighter

        // Check if any were added, deleted*, or changed
        let highlighter = currentHighlighters.get(highlighterID)

        if (highlighter) {
            // Highlighter already exists, checking and/or modifying
            thisHighlighterChanged = highlighter.updateFromFirestore(highlighterData)
        } else {
            // Highlighter does not exist in current, create it
            thisHighlighterChanged = true
            highlighter = Highlighter.fromFullhighlighter(highlighterData)
        }

        newHighlighters.set(highlighterID, highlighter)
        if (thisHighlighterChanged) highlightersChanged = true
    })

    if (currentHighlighters.size != newHighlighters.size) highlightersChanged = true // see if any were deleted

    if (highlightersChanged) highlighters.set(newHighlighters) // *This automatically handles deletion because any highlighters that were not present won't carry over
}

let unsubscribeFSHighlights: () => void = () => null

const highlightConverter = {
    toFirestore(highlighter: FSFullHighlighter) {
        return highlighter
    },
    fromFirestore(snapshot: QueryDocumentSnapshot): FSFullHighlighter {
        return snapshot.data() as FSFullHighlighter
    },
}

registerUserStatusCallback({
    unload: async () => {
        // Unsubscribe from listeners (this may be redundant)
        unsubscribeFSHighlights()

        debounceCancelList.forEach((debounceID) => debounceCancel(debounceID))
        debounceCancelList.clear()

        //Clear all highlighters
        highlightsReady.set(false)
        highlighters.set(new Map())
    },
    load: async (status) => {
        if (status == "out" || status == "unverified") {
            // user is logged out or has just logged out OR user is unverified (we only want to start sync if verified)

            setHighlighterStoreFromLS() // determine what's in localStorage, if none, do default
            highlightsReady.set(true)
        } else {
            // user is logged in or has just logged in from being signed out, possibly for the first time, might have to transfer from LS

            // Highlights
            const q = query(getHighlightsCollection()).withConverter(highlightConverter)

            // in case offline, we do want the first one to go through anyway.
            // This is a workaround for firestore's kind of buggy cache determination
            let firstQuerySnapshot = true

            return await new Promise<void>((resolveLoad) => {
                unsubscribeFSHighlights = onSnapshot(
                    q,
                    { includeMetadataChanges: false },
                    async (querySnapshot: QuerySnapshot<FSFullHighlighter>) => {
                        // Determine if source is external. Let the first one through regardless
                        const source = querySnapshot.metadata.hasPendingWrites ? "local" : "server"
                        // const cache = querySnapshot.metadata.fromCache
                        if (source == "local" && !firstQuerySnapshot) {
                            // bail if it's a local change (this means it's being written) AND it's not first. This should allow the first one to go thru always
                            firstQuerySnapshot = false
                            return
                        }
                        firstQuerySnapshot = false

                        // Wait for merge
                        await mergeFromDatabase(querySnapshot)

                        // Resolve to indicate readiness to load function
                        resolveLoad()
                        highlightsReady.set(true)
                    },
                )
            })
        }
    },
})
